/**
 * InstructorMoneyConfirmations — Payment & Booking Confirmations feed
 * (Feb 28, 2026).
 *
 * Calm, mobile-first receipt feed surfacing every booking/payment
 * row the instructor needs to recognise:
 *   • Who paid?
 *   • What did they pay for?
 *   • When was it booked?
 *   • What reference proves it?
 *   • What still needs payment?
 *
 * Reads from the canonical `lesson_events` collection via
 *   GET /api/instructor/money/confirmations
 * — no duplicate payment truth, no client-side aggregation.
 *
 * Doctrine:
 *   • Operational proof, not a chatty message feed.
 *   • Newest first. Mobile-first cards.
 *   • Empty state explains *why* it's empty calmly.
 *   • Filters never show dead chips — they only change the query.
 */

import { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { useTruthState } from "../hooks/useTruthState";
import { TruthReceipt } from "../components/TruthReceipt";
import {
  ArrowLeft,
  ReceiptText,
  Search,
  CheckCircle2,
  Clock4,
  XCircle,
  Undo2,
  HandCoins,
  Calendar,
  Hash,
  Copy,
  ChevronRight,
} from "lucide-react";
import InstructorBottomNav from "../components/instructor/InstructorBottomNav";
import { useAuth } from "../context/AuthContext";

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_FILTERS = [
  { id: "all", label: "All" },
  { id: "paid", label: "Paid" },
  { id: "initiated", label: "Pending" },
  { id: "payment_due", label: "Owed" },
  { id: "failed", label: "Failed" },
  { id: "refunded", label: "Refunded" },
];

const STATUS_META = {
  paid:               { label: "Paid",              tone: "emerald", Icon: CheckCircle2 },
  initiated:          { label: "Pending",           tone: "amber",   Icon: Clock4 },
  payment_due:        { label: "Owed",              tone: "amber",   Icon: HandCoins },
  failed:             { label: "Failed",            tone: "rose",    Icon: XCircle },
  refunded:           { label: "Refunded",          tone: "slate",   Icon: Undo2 },
  partially_refunded: { label: "Partial refund",    tone: "slate",   Icon: Undo2 },
  unknown:            { label: "—",                 tone: "slate",   Icon: ReceiptText },
};

const TONES = {
  emerald: "bg-emerald-50 text-emerald-700 ring-emerald-200",
  amber:   "bg-amber-50 text-amber-700 ring-amber-200",
  rose:    "bg-rose-50 text-rose-700 ring-rose-200",
  slate:   "bg-slate-100 text-slate-700 ring-slate-200",
};

const TYPE_LABEL = {
  payment_confirmation: "Payment confirmation",
  booking_confirmation: "Booking confirmation",
  gap_booked:           "Gap booked",
};

function formatDate(dateStr) {
  if (!dateStr) return "";
  try {
    const d = new Date(`${dateStr}T00:00:00`);
    return d.toLocaleDateString("en-GB", {
      weekday: "short", day: "numeric", month: "short", year: "numeric",
    });
  } catch {
    return dateStr;
  }
}

function formatRelative(iso) {
  if (!iso) return "";
  try {
    const t = new Date(iso).getTime();
    const diff = Date.now() - t;
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return "just now";
    if (mins < 60) return `${mins} min ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs} h ago`;
    const days = Math.floor(hrs / 24);
    if (days < 7) return `${days} d ago`;
    return new Date(iso).toLocaleDateString("en-GB", {
      day: "numeric", month: "short",
    });
  } catch {
    return "";
  }
}

export default function InstructorMoneyConfirmations() {
  const nav = useNavigate();
  const { user } = useAuth();
  const role = (user?.role || "adi").toLowerCase();

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [statusFilter, setStatusFilter] = useState("all");
  const [searchQ, setSearchQ] = useState("");
  const [debouncedQ, setDebouncedQ] = useState("");

  // Batch P1 · Money Truth-State — canonical async receipt for
  // every Money-domain action. Local completion (e.g. clipboard
  // reference copy) maps to `completed`; failures to `failed`.
  const truth = useTruthState();
  useEffect(() => {
    if (truth.state === "completed") {
      const t = setTimeout(() => truth.setState(null), 1200);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [truth]);

  // Debounce the search input so we don't fire one request per keystroke.
  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(searchQ.trim()), 250);
    return () => clearTimeout(t);
  }, [searchQ]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (statusFilter !== "all") params.set("status", statusFilter);
      if (debouncedQ) params.set("q", debouncedQ);
      params.set("limit", "50");
      const { data } = await axios.get(
        `${API}/api/instructor/money/confirmations?${params.toString()}`,
        { withCredentials: true },
      );
      setItems(Array.isArray(data?.items) ? data.items : []);
    } catch (e) {
      setError(
        e?.response?.data?.detail ||
          "Couldn't load payment confirmations right now.",
      );
    } finally {
      setLoading(false);
    }
  }, [statusFilter, debouncedQ]);

  useEffect(() => { load(); }, [load]);

  const isEmpty = !loading && !error && items.length === 0;
  const hasActiveFilter = statusFilter !== "all" || debouncedQ.length > 0;

  const headerSub = useMemo(() => {
    if (loading) return "Loading…";
    if (error) return error;
    if (items.length === 0) return "0 confirmations";
    return `${items.length} confirmation${items.length === 1 ? "" : "s"} · newest first`;
  }, [loading, error, items.length]);

  return (
    <div
      data-testid="instructor-money-confirmations-page"
      className="min-h-dvh no-overflow-x bg-slate-50 dark:bg-[#0c1120] pb-24"
    >
      <header className="bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-800 sticky top-0 z-30">
        <div className="max-w-lg mx-auto px-4 h-14 flex items-center gap-3">
          <button
            data-testid="confirmations-back-btn"
            onClick={() => nav("/instructor/money")}
            className="text-slate-400 hover:text-slate-700 dark:hover:text-white"
            aria-label="Back to Money"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <ReceiptText className="w-5 h-5 text-emerald-600" />
          <div className="flex-1 min-w-0">
            <p className="font-extrabold text-slate-900 dark:text-white text-base leading-tight">
              Payment confirmations
            </p>
            <p className="text-[11px] text-slate-500 truncate">{headerSub}</p>
          </div>
        </div>
      </header>

      <main className="max-w-lg mx-auto px-4 pt-3 space-y-3">
        {/* Batch P1 · Money Truth-State — canonical async receipt. */}
        <TruthReceipt
          state={truth.state}
          message={truth.message}
          onRetry={() => truth.setState(null)}
          onDismiss={() => truth.setState(null)}
        />

        {/* Search */}
        <div
          data-testid="confirmations-search"
          className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl px-3 py-2 flex items-center gap-2"
        >
          <Search className="w-4 h-4 text-slate-400 flex-shrink-0" />
          <input
            data-testid="confirmations-search-input"
            type="text"
            value={searchQ}
            onChange={(e) => setSearchQ(e.target.value)}
            placeholder="Search by pupil name"
            className="flex-1 bg-transparent text-sm text-slate-900 dark:text-white placeholder:text-slate-400 focus:outline-none"
          />
        </div>

        {/* Filter chips */}
        <div
          data-testid="confirmations-filters"
          className="flex gap-2 overflow-x-auto -mx-4 px-4 pb-1"
        >
          {STATUS_FILTERS.map((f) => {
            const active = statusFilter === f.id;
            return (
              <button
                key={f.id}
                data-testid={`confirmations-filter-${f.id}`}
                onClick={() => setStatusFilter(f.id)}
                className={
                  "shrink-0 px-3 py-1.5 rounded-full text-xs font-bold transition-all " +
                  (active
                    ? "bg-slate-900 text-white dark:bg-white dark:text-slate-900"
                    : "bg-white dark:bg-slate-900 text-slate-600 dark:text-slate-300 border border-slate-200 dark:border-slate-800 hover:border-emerald-300")
                }
              >
                {f.label}
              </button>
            );
          })}
        </div>

        {/* States */}
        {loading && (
          <div data-testid="confirmations-loading" className="py-12 text-center text-sm text-slate-500">
            Loading confirmations…
          </div>
        )}

        {!loading && error && (
          <div
            data-testid="confirmations-error"
            className="bg-rose-50 dark:bg-rose-900/20 border border-rose-200 dark:border-rose-800 rounded-xl p-4 text-sm text-rose-700 dark:text-rose-300"
          >
            <p className="font-bold">Something went wrong</p>
            <p className="text-xs mt-1">{error}</p>
            <button
              data-testid="confirmations-retry-btn"
              onClick={load}
              className="mt-3 inline-flex items-center gap-1 text-xs font-bold underline"
            >
              Try again
            </button>
          </div>
        )}

        {isEmpty && (
          <div
            data-testid="confirmations-empty-state"
            className="bg-white dark:bg-slate-900 border border-dashed border-slate-200 dark:border-slate-700 rounded-2xl p-6 text-center"
          >
            <ReceiptText className="w-8 h-8 text-slate-400 mx-auto mb-2" />
            <p className="text-sm font-bold text-slate-700 dark:text-slate-200">
              No payment confirmations yet
            </p>
            <p className="text-xs text-slate-500 mt-1 leading-relaxed">
              {hasActiveFilter
                ? "No confirmations match this filter. Try clearing it."
                : "When pupils book or pay for lessons, the receipts appear here — newest first."}
            </p>
            {hasActiveFilter && (
              <button
                data-testid="confirmations-clear-filters-btn"
                onClick={() => { setStatusFilter("all"); setSearchQ(""); }}
                className="mt-3 text-xs font-bold text-emerald-700 hover:underline"
              >
                Clear filters
              </button>
            )}
          </div>
        )}

        {!loading && !error && items.length > 0 && (
          <ul
            data-testid="confirmations-list"
            className="space-y-2"
          >
            {items.map((it) => (
              <ConfirmationCard key={it.id || it.lesson_id} item={it} truth={truth} />
            ))}
          </ul>
        )}
      </main>

      <InstructorBottomNav role={role} active="money" />
    </div>
  );
}

function ConfirmationCard({ item, truth }) {
  const nav = useNavigate();
  const meta = STATUS_META[item.payment_status] || STATUS_META.unknown;
  const tone = TONES[meta.tone] || TONES.slate;
  const StatusIcon = meta.Icon;
  const typeLabel = TYPE_LABEL[item.type] || "Confirmation";

  const open = () => {
    if (item.lesson_id) nav(`/lesson/${item.lesson_id}`);
  };

  const copyReference = (e) => {
    e.stopPropagation();
    if (!item.payment_reference) return;
    try {
      navigator.clipboard.writeText(item.payment_reference);
      truth?.setState?.("completed", "Reference copied.");
    } catch {
      truth?.setState?.("failed", "Couldn't copy — try again.");
    }
  };

  return (
    <li
      data-testid={`confirmation-card-${item.id}`}
      onClick={open}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") open(); }}
      className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl p-3 active:scale-[0.99] transition-all cursor-pointer hover:border-emerald-300"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-0.5">
            <span
              data-testid={`confirmation-card-${item.id}-type`}
              className="text-[10px] font-bold uppercase tracking-wider text-slate-500"
            >
              {typeLabel}
            </span>
            {item.is_manual_marked && (
              <span
                data-testid={`confirmation-card-${item.id}-manual-badge`}
                className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-600"
              >
                Manual
              </span>
            )}
          </div>
          <p className="font-bold text-slate-900 dark:text-white text-sm leading-tight truncate">
            {item.pupil_name}
          </p>
          {(item.lesson_date || item.lesson_start_time) && (
            <p
              data-testid={`confirmation-card-${item.id}-lesson-when`}
              className="text-[11px] text-slate-500 mt-0.5 flex items-center gap-1 truncate"
            >
              <Calendar className="w-3 h-3 flex-shrink-0" />
              {formatDate(item.lesson_date)}
              {item.lesson_start_time ? ` · ${item.lesson_start_time}` : ""}
            </p>
          )}
        </div>
        <div className="text-right flex-shrink-0">
          {item.amount_formatted && (
            <p
              data-testid={`confirmation-card-${item.id}-amount`}
              className="font-bold text-slate-900 dark:text-white text-sm"
            >
              {item.amount_formatted}
            </p>
          )}
          <span
            data-testid={`confirmation-card-${item.id}-status`}
            className={
              "mt-0.5 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold ring-1 " +
              tone
            }
          >
            <StatusIcon className="w-3 h-3" />
            {meta.label}
          </span>
        </div>
      </div>

      {(item.payment_reference || item.payment_method) && (
        <div className="mt-2 pt-2 border-t border-slate-100 dark:border-slate-800 flex items-center justify-between gap-2">
          {item.payment_reference ? (
            <button
              data-testid={`confirmation-card-${item.id}-copy-ref`}
              onClick={copyReference}
              className="flex items-center gap-1 text-[11px] text-slate-500 hover:text-emerald-700 truncate min-w-0"
              title={item.payment_reference}
            >
              <Hash className="w-3 h-3 flex-shrink-0" />
              <span className="truncate font-mono">{item.payment_reference}</span>
              <Copy className="w-3 h-3 flex-shrink-0 opacity-60" />
            </button>
          ) : (
            <span className="text-[11px] text-slate-400">No reference yet</span>
          )}
          {item.payment_method && (
            <span className="text-[11px] text-slate-400 capitalize flex-shrink-0">
              {item.payment_method}
            </span>
          )}
        </div>
      )}

      <div className="mt-1.5 flex items-center justify-between gap-2 text-[10px] text-slate-400">
        <span>{formatRelative(item.last_updated_at)}</span>
        <ChevronRight className="w-3 h-3" />
      </div>
    </li>
  );
}
